import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import SingleDeliveryItem from './SingleDeliveryItem';
import {
  CheckoutDateInputs,
  DeliveryComment,
  SingleDeliverySiloForm,
} from '@web/utils';
import { UserSilo } from '@/hooks/useUserSilosAndAddresses';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { Address, DeliveryType } from '@/types';
import {
  CartDelivery,
  CartDeliveryAddress,
  getSortedCartItems,
} from '@agrantis/cart';
import { updateCartDelivery } from '@agrantis/cart/actions/cart';
import {
  CalculatedCartDelivery,
  CalculatedCartItem,
} from '@agrantis/cart/selectors';

const SHOW_ADDRESS = false;

interface Props {
  onAddressChange: (address: CartDeliveryAddress, deliveryId: string) => void;
  onEditItem: (item: CalculatedCartItem, delivery: CartDelivery) => void;
  onLocationChange: (location: number, deliveryId: string) => void;
  onCommentChange: (comment: string, deliveryId: string) => void;
  primaryAddress?: { id: number; address: CartDeliveryAddress };
  onSiloChange: (siloId: number, deliveryId: string) => void;
  addresses: { id: number; address: CartDeliveryAddress }[];
  onFromTime: (time: string, deliveryId: string) => void;
  onToTime: (time: string, deliveryId: string) => void;
  delivery: CalculatedCartDelivery;
  silos: UserSilo[];
  index: number;
  cartKey: string;
}

const SingleDelivery: React.FC<Props> = (props) => {
  const {
    onCommentChange,
    onSiloChange,
    onFromTime,
    onToTime,
    addresses,
    delivery,
    silos,
    index,
    cartKey,
    onAddressChange,
    primaryAddress,
  } = props;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.user.data);
  const retailer = useAppSelector((state) => state.retailer.retailer);

  const addressLine = (
    address: Omit<Address, 'owner' | 'ownerId' | 'id'> | null,
  ) => {
    if (!address) return '';
    return `${address.addressLine1}, ${address.addressLine2 || ''} ${
      address.addressLocality
    }, ${address.addressPostcode}`;
  };

  const handleFromDateChange = (date: string) => {
    onFromTime(date, delivery.id);
  };

  const handleToDateChange = (date: string) => {
    onToTime(date, delivery.id);
  };

  const changeDeliveryDates = (key: string, value: string | number) => {
    if (key === 'from') {
      handleFromDateChange(value.toString());
    } else {
      handleToDateChange(value.toString());
    }
  };

  const handleSiloChange = (siloId: string) => {
    onSiloChange(parseInt(siloId), delivery.id);
  };

  const handleCommentUpdate = (comment: string) => {
    onCommentChange(comment, delivery.id);
  };

  const onAddressPick = (addressId: number) => {
    const address = addresses.find((i) => i.id === addressId)?.address;
    if (address) {
      onAddressChange(address, delivery.id);
    }
  };

  useEffect(() => {
    if (
      delivery.type === DeliveryType.Delivery &&
      !delivery.siloId &&
      delivery.deliveryAddress === null &&
      primaryAddress?.address
    ) {
      dispatch(
        updateCartDelivery({
          deliveryId: delivery.id,
          cartKey,
          deliveryAddress: primaryAddress?.address,
          requiredDeliveryDate: retailer?.settings?.requiredDeliveryDate,
        }),
      );
    }
  }, [
    primaryAddress,
    cartKey,
    delivery,
    dispatch,
    retailer?.settings?.requiredDeliveryDate,
  ]);

  const title = useMemo(
    () =>
      `${
        delivery.type === DeliveryType.Pickup
          ? t('order.pickup')
          : t('order.delivery')
      } #${index}`,
    [delivery.type, index, t],
  );

  const addressOptions = user?.addresses.map((i) => ({
    id: i.id,
    name: addressLine(i),
  }));

  const showDeliveryDateInputs = useMemo(() => {
    const requiredDeliveryDate = retailer?.settings?.requiredDeliveryDate;

    return delivery.siloId || requiredDeliveryDate;
  }, [delivery.siloId, retailer?.settings?.requiredDeliveryDate]);

  return (
    <div className="mb-4-5" id={delivery.id.toString()}>
      <p className="text-body1 font-bd mb-1-6">{title}</p>
      <div className="flex justify-between items-start">
        {SHOW_ADDRESS &&
          delivery.type === DeliveryType.Delivery &&
          !delivery.siloId && (
            <div>
              <div className="mb-1-0">
                <p className="mb-0-6 text-2-0 md:text-1-2 leading-1-33 font-li">
                  {t('checkout.date_and_address')}
                </p>
                <select
                  className="w-full max-w-30-0 rounded-8-0 py-0-8 px-1-4 border-secondary-grey text-body1 leading-1-25 border bg-white"
                  onChange={(e) => onAddressPick(parseInt(e.target.value))}
                  value={
                    addressOptions?.find(
                      (a) => a.name === addressLine(delivery.deliveryAddress),
                    )?.id
                  }
                  data-testid={`delivery-select-${delivery.id}`}
                >
                  {addressOptions?.map((address) => (
                    <option
                      key={address.id}
                      value={address.id}
                      className="w-full"
                    >
                      {address.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        <SingleDeliverySiloForm
          delivery={delivery}
          changeDeliverySilo={handleSiloChange}
          t={t}
          silos={silos}
        />
        {showDeliveryDateInputs && (
          <CheckoutDateInputs
            handleSiloDateUpdate={changeDeliveryDates}
            t={t}
            deliveryFrom={delivery.deliveryFrom}
            deliveryTo={delivery.deliveryTo}
          />
        )}
      </div>
      <div className="mt-1-0 border border-secondary-grey rounded-8-0 p-1-0">
        <div className="hidden py-3-0 md:py-1-0 border-b last:border-0 border-secondary-grey md:grid grid-cols-[1fr_1fr_1fr_1fr] items-center relative">
          <div className="flex mb-2-0 md:mb-0 pr-3-0 md:pr-0">
            <span className="text-1-4 font-rg">
              {t('checkout.product_name')}
            </span>
          </div>
          <div>
            <span className="text-1-4 font-rg">{t('checkout.amount')}</span>
          </div>
          <div>
            <span className="text-1-4 font-rg">{t('checkout.cost')}</span>
          </div>
          <div />
        </div>
        {getSortedCartItems(delivery.items).map((item: CalculatedCartItem) => (
          <SingleDeliveryItem
            item={item}
            silos={silos}
            delivery={delivery}
            key={item.itemData.id}
          />
        ))}
      </div>
      <DeliveryComment
        t={t}
        handleAddCommentToDelivery={handleCommentUpdate}
        isEdit={!!delivery.comment}
        comment={delivery.comment}
      />
    </div>
  );
};

export default SingleDelivery;
